import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setTokens, clearTokens, getRefreshToken } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const triggerSplash = useCallback(() => setShowSplash(true), []);
  const endSplash = useCallback(() => setShowSplash(false), []);
  const endLogout = useCallback(() => setShowLogout(false), []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      // access token may be expired — try one refresh (works via cookie or X-Refresh-Token)
      if (getRefreshToken()) {
        try {
          const { data: rt } = await api.post("/auth/refresh", null, {
            headers: { "X-Refresh-Token": getRefreshToken() },
          });
          if (rt?.access_token) setTokens(rt.access_token, null);
          const { data } = await api.get("/auth/me");
          setUser(data);
          return data;
        } catch { clearTokens(); }
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    if (data.two_factor_required) return data;
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setShowSplash(true);
    return data.user;
  };

  const verify2fa = async (challenge_id, code, remember_device = false) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_id, code, remember_device });
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setShowSplash(true);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setShowSplash(true);
    return data.user;
  };

  const logout = async () => {
    setShowLogout(true);
    try { await api.post("/auth/logout"); } catch {}
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refreshUser, login, verify2fa, register, logout, showSplash, triggerSplash, endSplash, showLogout, endLogout }}>
      {children}
    </AuthContext.Provider>
  );
}
